import { useRef, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import { ClearAttributeMappingsPayload } from 'services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import { MappingActionsToolbarProps } from './MappingActionsToolbar.props';
import {
  acceptAllButtonGroupSx,
  acceptAllButtonSx,
  acceptAllConfirmButtonSx,
  acceptAllDropdownSx,
  createLabelsPrimarySplitButtonSx,
  createLabelsSplitGroupSx,
  createLabelsTextSx,
  dropdownChevronButtonSx,
  popperSx,
  toolbarContainerSx
} from './MappingActionsToolbar.styles';
import { XLSX_VIEWER_STRINGS } from './xlsx-viewer.contants';

const MappingActionsToolbar = ({
  documentReferenceId,
  activeSheetLabel,
  onClearMappings,
  onAcceptAllAISuggestions,
  hasPendingAISuggestions,
  onDeclineAllAISuggestions,
  onReprocessDocument,
  showCreateLabels = true,
}: MappingActionsToolbarProps) => {
  const [open, setOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingClearPayload, setPendingClearPayload] = useState<ClearAttributeMappingsPayload>();
  const [acceptAllDialogOpen, setAcceptAllDialogOpen] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleOpenConfirmDialog = (payload?: ClearAttributeMappingsPayload) => {
    setPendingClearPayload(payload);
    setConfirmDialogOpen(true);
    setOpen(false);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setPendingClearPayload(undefined);
  };

  const handleConfirmClearMappings = () => {
    onClearMappings(pendingClearPayload);
    handleCloseConfirmDialog();
  };

  const handleCreateLabels = () => {
    onReprocessDocument?.();
    setOpen(false);
  };

  const handleAcceptAll = () => {
    setAcceptAllDialogOpen(true);
  };

  const handleCloseAcceptAllDialog = () => {
    if (isAccepting) {
      return;
    }
    setAcceptAllDialogOpen(false);
  };

  const handleConfirmAcceptAll = async () => {
    setIsAccepting(true);
    try {
      await onAcceptAllAISuggestions?.();
    } finally {
      setIsAccepting(false);
      setAcceptAllDialogOpen(false);
    }
  };

  return (
    <>
      <Box sx={toolbarContainerSx}>
        {/* ACCEPT ALL split button */}
        <ButtonGroup ref={anchorRef} variant="contained" size="small" disableElevation sx={acceptAllButtonGroupSx}>
          <Button onClick={handleAcceptAll} sx={acceptAllButtonSx} disabled={isAccepting || !hasPendingAISuggestions}>
            <Box
              component="img"
              src="/images/scalar_ai_white.png"
              alt="Scalar AI"
              sx={{ width: 16, height: 16, mr: 0.75 }}
            />
            {XLSX_VIEWER_STRINGS.BUTTON.ACCEPT_ALL}
          </Button>
          <Button onClick={handleToggle} sx={acceptAllDropdownSx} aria-haspopup="menu">
            <ArrowDropDownIcon fontSize="small" />
          </Button>
        </ButtonGroup>
        {/* CREATE LABELS button with dropdown */}
        {showCreateLabels && (
          <Box sx={createLabelsSplitGroupSx}>
            <Button
              variant="text"
              size="small"
              onClick={handleCreateLabels}
              sx={createLabelsPrimarySplitButtonSx}
              aria-haspopup="menu">
              <Typography component="span" sx={createLabelsTextSx}>
                {XLSX_VIEWER_STRINGS.BUTTON.CREATE_LABELS}
              </Typography>
            </Button>
            <Button
              ref={anchorRef}
              variant="text"
              size="small"
              sx={dropdownChevronButtonSx}
              onClick={handleToggle}
              endIcon={<ArrowDropDownIcon />}
              aria-controls={open ? XLSX_VIEWER_STRINGS.ARIA.MENU_ID : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-haspopup="menu">
              <></>
            </Button>
          </Box>
        )}

        {/* PUSH MAPPINGS button — kept for functionality reference, hidden from UI */}
        {/* <Tooltip title="Push Mappings">
          <Button variant="contained" color="success" size="small" onClick={onPushMappings}>
            {XLSX_VIEWER_STRINGS.BUTTON.PUSH_MAPPINGS}
          </Button>
        </Tooltip> */}
      </Box>

      {/* DROPDOWN MENU */}
      <Popper
        sx={popperSx}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-end">
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem>
                  <MenuItem
                    onClick={() =>
                      handleOpenConfirmDialog({
                        documentReferenceId,
                        sheet: `${activeSheetLabel}`,
                      })
                    }>
                    {XLSX_VIEWER_STRINGS.MENU.CLEAR_SHEET} {activeSheetLabel ? `(${activeSheetLabel})` : ''}{' '}
                    {XLSX_VIEWER_STRINGS.MENU.CLEAR_SHEET_SUFFIX}
                  </MenuItem>
                  <MenuItem onClick={() => handleOpenConfirmDialog({ documentReferenceId })}>
                    {XLSX_VIEWER_STRINGS.MENU.CLEAR_DOCUMENT}
                  </MenuItem>
                  <MenuItem onClick={() => handleOpenConfirmDialog()}>{XLSX_VIEWER_STRINGS.MENU.CLEAR_ALL}</MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      {/* Confirmation dialog for accept all AI suggestions */}
      <Dialog open={acceptAllDialogOpen} onClose={handleCloseAcceptAllDialog}>
        <DialogTitle>{XLSX_VIEWER_STRINGS.ACCEPT_ALL_DIALOG.TITLE}</DialogTitle>
        <DialogContent>
          <DialogContentText>{XLSX_VIEWER_STRINGS.ACCEPT_ALL_DIALOG.CONTENT}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAcceptAllDialog} color="primary" disabled={isAccepting}>
            {XLSX_VIEWER_STRINGS.ACCEPT_ALL_DIALOG.CANCEL}
          </Button>
          <Button
            onClick={handleConfirmAcceptAll}
            sx={acceptAllConfirmButtonSx}
            variant="contained"
            disabled={isAccepting}>
            {isAccepting ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
            {XLSX_VIEWER_STRINGS.ACCEPT_ALL_DIALOG.CONFIRM}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog for clear actions */}
      <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
        <DialogTitle>{XLSX_VIEWER_STRINGS.DIALOG.TITLE}</DialogTitle>
        <DialogContent>
          <DialogContentText>{XLSX_VIEWER_STRINGS.DIALOG.CONTENT}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            {XLSX_VIEWER_STRINGS.DIALOG.CANCEL}
          </Button>
          <Button onClick={handleConfirmClearMappings} color="error" variant="contained">
            {XLSX_VIEWER_STRINGS.DIALOG.CONFIRM}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MappingActionsToolbar;
